import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Building2, Printer, MessageSquare, ShieldAlert, UsersRound, Save, Loader2, Plus, X, RotateCcw, GraduationCap } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { usePasses } from "../context/PassesContext";
import { useTalmidim } from "../context/TalmidimContext";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import UsersManagement from "../components/UsersManagement";
import ClassesManagement from "../components/ClassesManagement";
import { normalizeReasons, CATEGORY_JUSTIFIED, CATEGORY_UNJUSTIFIED, CATEGORY_LABEL_HE } from "../lib/reasons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { settings, updateSettings, resetSettings } = useSettings();
  const { refresh: refreshPasses } = usePasses();
  const { refresh: refreshTalmidim } = useTalmidim();
  const { isAdmin } = useAuth();
  const { toast } = useToast();

  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [newShortcut, setNewShortcut] = useState("");
  const [newReason, setNewReason] = useState("");

  useEffect(() => { setForm(settings); }, [settings]);

  const setField = (key, value) => setForm((s) => ({ ...s, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await updateSettings(form);
      toast({ title: "Settings saved", description: "Your changes have been applied" });
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Save failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const addShortcut = () => {
    const v = newShortcut.trim();
    if (!v) return;
    if ((form.reasonShortcuts || []).length >= 6) {
      toast({ title: "Max 6 shortcuts", variant: "destructive" });
      return;
    }
    setField("reasonShortcuts", [...(form.reasonShortcuts || []), v]);
    setNewShortcut("");
  };

  const removeShortcut = (i) => {
    const next = [...(form.reasonShortcuts || [])];
    next.splice(i, 1);
    setField("reasonShortcuts", next);
  };

  const addReason = (category) => {
    const v = newReason.trim();
    if (!v) return;
    const list = normalizeReasons(form.commonReasons);
    // Prevent duplicates (same text, any category)
    if (list.some((r) => r.text === v)) {
      toast({ title: "Duplicate", description: `"${v}" is already in the list`, variant: "destructive" });
      return;
    }
    setField("commonReasons", [...list, { text: v, category }]);
    setNewReason("");
  };

  const removeReason = (text) => {
    const list = normalizeReasons(form.commonReasons).filter((r) => r.text !== text);
    setField("commonReasons", list);
  };

  const moveReason = (text, toCategory) => {
    const list = normalizeReasons(form.commonReasons).map((r) =>
      r.text === text ? { ...r, category: toCategory } : r
    );
    setField("commonReasons", list);
  };

  const dangerClear = async (kind) => {
    try {
      const url = kind === "passes" ? `${API}/danger/clear-passes` : `${API}/danger/clear-talmidim`;
      const res = await axios.post(url, { confirm: "DELETE" });
      toast({ title: "Deleted", description: `${res.data.deleted} records removed` });
      if (kind === "passes") await refreshPasses(); else await refreshTalmidim();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Failed", variant: "destructive" });
    }
  };

  const doReset = async () => {
    await resetSettings();
    toast({ title: "Settings reset", description: "All settings restored to defaults" });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif-heb text-3xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-500 text-sm mt-1">Customize your Late Pass System</p>
        </div>
        <Button onClick={save} disabled={saving} className="gradient-blue-mild text-white">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <Tabs defaultValue="school">
        <TabsList className={`grid ${isAdmin ? "grid-cols-6" : "grid-cols-5"} w-full max-w-3xl`}>
          <TabsTrigger value="school"><Building2 className="w-4 h-4 mr-2" />School Info</TabsTrigger>
          <TabsTrigger value="receipt"><Printer className="w-4 h-4 mr-2" />Receipt</TabsTrigger>
          <TabsTrigger value="reasons"><MessageSquare className="w-4 h-4 mr-2" />Reasons</TabsTrigger>
          <TabsTrigger value="classes"><GraduationCap className="w-4 h-4 mr-2" />Classes</TabsTrigger>
          {isAdmin && <TabsTrigger value="users"><UsersRound className="w-4 h-4 mr-2" />Users</TabsTrigger>}
          <TabsTrigger value="danger"><ShieldAlert className="w-4 h-4 mr-2" />Danger</TabsTrigger>
        </TabsList>

        {/* SCHOOL INFO */}
        <TabsContent value="school" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>School Information</CardTitle>
              <CardDescription>This appears on the login page and printed receipts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">School Name (Hebrew/Yiddish)</label>
                <Input value={form.schoolName || ""} onChange={(e) => setField("schoolName", e.target.value)} dir="rtl" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">School Name (English)</label>
                <Input value={form.schoolNameEn || ""} onChange={(e) => setField("schoolNameEn", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">School Address</label>
                <Input value={form.schoolAddress || ""} onChange={(e) => setField("schoolAddress", e.target.value)} placeholder="1234 Main St, Brooklyn, NY" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">School Phone</label>
                <Input value={form.schoolPhone || ""} onChange={(e) => setField("schoolPhone", e.target.value)} placeholder="718-555-0100" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Admin Name (Signed by)</label>
                <Input value={form.adminName || ""} onChange={(e) => setField("adminName", e.target.value)} dir="rtl" />
                <div className="text-xs text-slate-500 mt-1">Appears as the signer on every late pass</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* RECEIPT SETTINGS */}
        <TabsContent value="receipt" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Receipt Display</CardTitle>
              <CardDescription>Customize what shows on the 80mm printed receipt</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                <div>
                  <div className="font-medium text-slate-800">Show school logo</div>
                  <div className="text-xs text-slate-500">Display the Ohr Hatorah crest at the top</div>
                </div>
                <Switch checked={!!form.showLogoOnReceipt} onCheckedChange={(v) => setField("showLogoOnReceipt", v)} />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                <div>
                  <div className="font-medium text-slate-800">Show signature line</div>
                  <div className="text-xs text-slate-500">A blank line labeled "חתימה" at the bottom</div>
                </div>
                <Switch checked={!!form.showSignatureLine} onCheckedChange={(v) => setField("showSignatureLine", v)} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Receipt footer message</label>
                <Textarea value={form.receiptFooter || ""} onChange={(e) => setField("receiptFooter", e.target.value)} placeholder="Optional: a message at the bottom of every receipt" rows={2} dir="rtl" />
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-slate-600">
                <div className="font-medium text-slate-700 mb-1">Printer note</div>
                Optimized for the Milestone MHT-P80E thermal printer (80mm). Use your OS print dialog to select the thermal printer.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* REASONS */}
        <TabsContent value="reasons" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick Reason Shortcuts</CardTitle>
              <CardDescription>Buttons shown above the reason dropdown on the New Late Pass form (max 6)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {(form.reasonShortcuts || []).map((r, i) => (
                  <div key={i} className="flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-800 rounded-full pl-3 pr-1 py-1" dir="rtl">
                    <span className="text-sm">{r}</span>
                    <button onClick={() => removeShortcut(i)} className="w-6 h-6 rounded-full hover:bg-blue-100 flex items-center justify-center">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newShortcut} onChange={(e) => setNewShortcut(e.target.value)} placeholder="Add shortcut (Hebrew or English)" dir="rtl"
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addShortcut())} />
                <Button variant="outline" onClick={addShortcut}><Plus className="w-4 h-4 mr-1" />Add</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Full Reason List</CardTitle>
              <CardDescription>
                Categorize each reason. Category is shown on the printed pass.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex gap-2">
                <Input
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  placeholder="Type a reason then click Add ↓"
                  dir="rtl"
                  data-testid="new-reason-input"
                />
              </div>

              {[CATEGORY_JUSTIFIED, CATEGORY_UNJUSTIFIED].map((cat) => {
                const list = normalizeReasons(form.commonReasons).filter((r) => r.category === cat);
                const isJustified = cat === CATEGORY_JUSTIFIED;
                return (
                  <div key={cat} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${isJustified ? "bg-emerald-500" : "bg-red-500"}`} />
                        <div className="text-sm font-semibold text-slate-800" dir="rtl">
                          {CATEGORY_LABEL_HE[cat]}
                        </div>
                        <span className="text-xs text-slate-500">
                          ({isJustified ? "excused" : "not excused"})
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addReason(cat)}
                        disabled={!newReason.trim()}
                        data-testid={`add-reason-${cat}-btn`}
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" />
                        Add to this category
                      </Button>
                    </div>
                    <div className="space-y-1.5">
                      {list.length === 0 && (
                        <div className="text-xs text-slate-400 italic py-2 pl-4">
                          No reasons in this category yet
                        </div>
                      )}
                      {list.map((r) => (
                        <div
                          key={r.text}
                          className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${
                            isJustified ? "bg-emerald-50/40 border-emerald-100" : "bg-red-50/40 border-red-100"
                          }`}
                          data-testid={`reason-row-${r.text}`}
                        >
                          <span className="flex-1 text-sm text-slate-800" dir="rtl">
                            {r.text}
                          </span>
                          <button
                            onClick={() =>
                              moveReason(r.text, isJustified ? CATEGORY_UNJUSTIFIED : CATEGORY_JUSTIFIED)
                            }
                            className="text-xs text-slate-600 hover:text-slate-900 hover:underline px-2"
                            title="Move to the other category"
                          >
                            {isJustified ? "→ נישט בארעכטיגט" : "→ בארעכטיגט"}
                          </button>
                          <button
                            onClick={() => removeReason(r.text)}
                            className="w-7 h-7 rounded hover:bg-red-50 text-red-500 flex items-center justify-center"
                            title="Remove"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* USERS */}
        {isAdmin && (
          <TabsContent value="users" className="mt-6">
            <UsersManagement />
          </TabsContent>
        )}

        {/* CLASSES */}
        <TabsContent value="classes" className="mt-6">
          <ClassesManagement />
        </TabsContent>

        {/* DANGER */}
        <TabsContent value="danger" className="mt-6">
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="text-red-700">Danger Zone</CardTitle>
              <CardDescription>Irreversible actions. Proceed with caution.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <DangerRow
                title="Reset all settings to default"
                desc="Restores school name, reasons, and toggles to factory defaults"
                actionLabel="Reset Settings"
                icon={RotateCcw}
                confirmTitle="Reset all settings?"
                confirmDesc="This will restore all settings to their default values. Your students and passes will not be affected."
                onConfirm={doReset}
              />
              <DangerRow
                title="Delete all late passes"
                desc="Removes every late pass record permanently (students remain)"
                actionLabel="Delete All Passes"
                icon={ShieldAlert}
                confirmTitle="Delete every late pass?"
                confirmDesc="This deletes ALL late pass records permanently. Students and settings remain untouched. This cannot be undone."
                onConfirm={() => dangerClear("passes")}
                destructive
              />
              <DangerRow
                title="Delete all students"
                desc="Removes every student record (passes remain but will have orphan references)"
                actionLabel="Delete All Students"
                icon={ShieldAlert}
                confirmTitle="Delete every student?"
                confirmDesc="This deletes ALL student records permanently. You will need to import them again. This cannot be undone."
                onConfirm={() => dangerClear("talmidim")}
                destructive
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DangerRow({ title, desc, actionLabel, icon: Icon, confirmTitle, confirmDesc, onConfirm, destructive }) {
  return (
    <div className={`p-4 rounded-lg border ${destructive ? "border-red-200 bg-red-50/50" : "border-slate-200"}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <div className={`font-medium ${destructive ? "text-red-900" : "text-slate-800"}`}>{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant={destructive ? "destructive" : "outline"} size="sm">
              <Icon className="w-4 h-4 mr-2" />{actionLabel}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
              <AlertDialogDescription>{confirmDesc}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction className={destructive ? "bg-red-600 hover:bg-red-700" : ""} onClick={onConfirm}>
                {actionLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
